import { FunctionComponent, useCallback, useState } from "react";
import {
  HiBadgeCheck,
  HiCheck,
  HiOutlineExclamation,
  HiOutlineExclamationCircle,
  HiOutlineUserAdd,
  HiX,
} from "react-icons/hi";

import Button from "../components/Button";
import LoadingPage from "../components/LoadingPage";
import Modal from "../components/Modal";

import useData from "../src/useData";
import useUser from "../src/useUser";
import useTenant from "../src/useTenant";
import { tenantDisplayName } from "../src/useTenant";

import { Tenant } from "../src/types";
import WithIcon from "../components/WithIcon";
import { useTenantAction } from "../src/useAction";
import Listbox from "./Listbox";

interface TenantUser {
  id: string;
  login_identifier: string;
  name: string;
  role: string;
  pending: boolean;
  owner: boolean;
}

const prettyLoginLabel = (tenant: Tenant) => {
  switch (tenant.authentication_provider) {
    case "github":
      return "GitHub login";
    case "gsuite":
      return "Google email";
    case "oidc":
      return "OIDC login";
  }
};

const prettyAuthenticationProvider = (tenant: Tenant) => {
  switch (tenant.authentication_provider) {
    case "github":
      return "GitHub";
    case "gsuite":
      return "Google";
    case "oidc":
      return "OIDC";
  }
};

const RoleSelector: FunctionComponent<{
  user: TenantUser;
  onChange: (user: TenantUser, role: string) => Promise<void>;
  onError: (msg: string) => void;
  onUpdate: () => void;
  className: string;
}> = ({ user, onChange, onError, onUpdate, className }) => {
  const [selected, setSelected] = useState(user.role);
  const [showApply, setShowApply] = useState(false);

  const roles = ["Admin", "Member"];

  function classNames(...classes: string[]) {
    return classes.filter(Boolean).join(" ");
  }

  return (
    <div className={classNames(className, "grid grid-cols-8 gap-2")}>
      <div className="col-span-6">
        <Listbox
          options={roles}
          onChange={(value) => {
            // Guaranteed not to be an array but satisfying static type checker
            if (!Array.isArray(value)) {
              setSelected(value);
              if (value !== user.role) {
                setShowApply(true);
              } else {
                setShowApply(false);
              }
            }
          }}
          selectedValue={selected}
          className="text-sm"
        />
      </div>
      {showApply && (
        <div className="col-span-2">
          <Button
            xsmall
            theme="primary"
            icon={<HiCheck />}
            onClick={async () => {
              try {
                await onChange(user, selected);
                setShowApply(false);
                onUpdate();
              } catch (e) {
                onError(`${e}`);
              }
            }}
          >
            Apply
          </Button>
        </div>
      )}
    </div>
  );
};

const UserDetails: FunctionComponent<{
  tenant: Tenant;
  tenantUser: TenantUser;
}> = ({ tenant, tenantUser }) => {
  switch (tenant.authentication_provider) {
    case "github":
      return (
        <a
          href={`https://github.com/${tenantUser.login_identifier}`}
          target="_blank"
          rel="noreferrer"
        >
          {tenantUser.login_identifier}
        </a>
      );
    case "gsuite":
    case "oidc":
      return <span>{tenantUser.login_identifier}</span>;
  }
};

const validateEmail = (email: string) => {
  return /\S+@\S+[.]\S+/.test(email);
};

const AddUserModal: FunctionComponent<{
  isOpen: boolean;
  onClose: () => void;
  onAdd: (login: string) => void;
  tenant: Tenant;
  placeholderLogin: string;
}> = ({ isOpen, tenant, onClose, placeholderLogin, onAdd }) => {
  const [login, setLogin] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const osoRoles = tenant.kind === "production";

  const closeAndReset = useCallback(() => {
    setError(null);
    setLogin("");
    setEmail("");
    setLoading(false);
    onClose();
  }, [setError, onClose, setLoading]);

  const addUser = useCallback(
    async (role: string) => {
      if (
        tenant.authentication_provider === "github" &&
        !validateEmail(email)
      ) {
        setError("Please enter a valid email address.");
        return;
      }

      // allow updating the email in place.
      let emailToUse = email;

      if (tenant.authentication_provider === "oidc") {
        // When SSO is enabled on the organization the email will default to the login if not provided.
        if (email.length === 0) {
          emailToUse = login;
        }
        if (!validateEmail(emailToUse)) {
          setError("Please enter a valid email address.");
          return;
        }
      }

      try {
        setLoading(true);

        const resp = await fetch(`/web/tenants/${tenant.id}/users/`, {
          method: "POST",
          body: JSON.stringify({
            login_identifier: login,
            email: emailToUse,
            role,
          }),
          headers: { "Content-Type": "application/json" },
        });
        if (!resp.ok) {
          throw `There was a problem adding ${login}. Please try again later.`;
        }

        onAdd(login);
        closeAndReset();
      } catch (e) {
        setLoading(false);
        setError(`${e}`);
      }
    },
    [tenant, setError, closeAndReset, onAdd, login, email]
  );

  return (
    <Modal isOpen={isOpen} onClose={closeAndReset}>
      <h1 className="text-xl font-space mb-3">
        Invite a user to {tenantDisplayName(tenant)}
      </h1>
      {osoRoles ? (
        <div className="mb-3">
          Oso Cloud supports two roles for users,{" "}
          <span className="font-bold">Admin</span> and{" "}
          <span className="font-bold">Member (default)</span>:
          <ul className="ml-6 list-disc list-outside">
            <li>
              <span className="font-bold">Admin users</span> have read-write
              permissions on all environments and management capabilities (e.g.,
              adding users).
            </li>
            <li>
              <span className="font-bold">Member users</span> have read-only
              permissions on the production environment and read-write
              permissions on other environments.
            </li>
          </ul>
        </div>
      ) : (
        <div className="mb-3">
          This user will be able to edit policies, edit and view facts, issue
          and revoke API keys, and manage users for{" "}
          <strong>{tenantDisplayName(tenant)}</strong>.
        </div>
      )}
      <div className="mb-3">
        <HiOutlineExclamationCircle className="inline mr-2" />
        <span className="align-middle">
          This user will need to sign into Oso Cloud via{" "}
          {prettyAuthenticationProvider(tenant)} to accept the invite.
        </span>
      </div>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          addUser(osoRoles ? "Member" : "Admin");
        }}
      >
        <div className="mb-3 space-x-2">
          <label htmlFor="login_identifier">{prettyLoginLabel(tenant)}:</label>
          <input
            className="border px-2 py-1 text-base rounded"
            placeholder={placeholderLogin}
            id="login_identifier"
            value={login}
            onChange={(e) => {
              setLogin(e.target.value.trim());
              setError(null);
            }}
          ></input>
        </div>
        {tenant.authentication_provider !== "gsuite" && (
          <div className="mb-3 space-x-2">
            <label htmlFor="login_identifier">
              {tenant.authentication_provider === "github"
                ? "Email"
                : "Email (optional)"}
            </label>
            <input
              className="border px-2 py-1 text-base rounded"
              placeholder="example@gmail.com"
              id="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value.trim());
                setError(null);
              }}
            ></input>
            {tenant.authentication_provider === "oidc" && (
              <p className="text-small my-4">
                <i>
                  When OIDC is enabled on the organization the email will
                  default to the login if not provided.
                </i>
              </p>
            )}
          </div>
        )}
        {error !== null && (
          <div className="mb-3 border border-red-600 rounded px-3 py-2 text-red-600">
            <HiOutlineExclamation className="inline mr-2" />
            <span className="align-middle">{error}</span>
          </div>
        )}
        <div className="flex space-x-2">
          <Button
            medium
            theme="primary"
            disabled={login.length == 0 || loading}
            loading={loading}
            type="submit"
            icon={<HiOutlineUserAdd />}
          >
            Invite user
          </Button>
          <Button medium onClick={closeAndReset} type="button">
            Cancel
          </Button>
        </div>
      </form>
    </Modal>
  );
};
const RemoveUserModal: FunctionComponent<{
  tenant: Tenant;
  userToRemove: TenantUser | null;
  onClose: () => void;
  onRemove: () => void;
}> = ({ tenant, userToRemove, onClose, onRemove }) => {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const close = useCallback(() => {
    onClose();
    setError(null);
    setLoading(false);
  }, [setError, onClose]);

  const removeUser = useCallback(async () => {
    if (userToRemove === null) {
      return;
    }
    try {
      setLoading(true);

      const resp = await fetch(`/web/tenants/${tenant.id}/users/`, {
        method: "DELETE",
        body: JSON.stringify({ external_user_id: userToRemove.id }),
        headers: { "Content-Type": "application/json" },
      });
      if (!resp.ok) {
        throw `There was a problem removing ${userToRemove.login_identifier}. Please try again later.`;
      }
      onRemove();
      close();
    } catch (e) {
      setLoading(false);
      setError(`${e}`);
    }
  }, [tenant, setError, userToRemove, onRemove, close, setLoading]);
  return (
    <Modal isOpen={userToRemove !== null} onClose={close}>
      <h1 className="text-xl font-space mb-3">
        Remove user {userToRemove?.name}?
      </h1>
      {userToRemove?.pending ? (
        <div className="mb-3">
          Are you sure you want to cancel the invitation for{" "}
          <strong>{userToRemove?.login_identifier}</strong> to{" "}
          <strong>{tenantDisplayName(tenant)}</strong>?
        </div>
      ) : (
        <>
          <div className="mb-3">
            Are you sure you want to remove{" "}
            <strong>{userToRemove?.name}</strong> from{" "}
            <strong>{tenantDisplayName(tenant)}</strong>?
          </div>
          <div className="mb-3">
            {userToRemove?.role === "Admin" && (
              <>
                This user will no longer be able to edit policies, edit and view
                facts, issue and revoke API keys, or manage users for{" "}
                <strong>{tenantDisplayName(tenant)}</strong>.{" "}
              </>
            )}
          </div>
        </>
      )}
      {error && (
        <div className="mb-3 border border-red-600 rounded px-3 py-2 text-red-600">
          <HiOutlineExclamation className="inline mr-2" />
          <span className="align-middle">{error}</span>
        </div>
      )}
      <div className="flex space-x-2">
        <Button
          medium
          theme="primary"
          onClick={removeUser}
          disabled={loading}
          loading={loading}
          icon={<HiX />}
        >
          Remove user
        </Button>
        <Button medium onClick={close}>
          Cancel
        </Button>
      </div>
    </Modal>
  );
};

const UserManagement: FunctionComponent = () => {
  const { user } = useUser();
  const { tenant } = useTenant();
  const { data, refresh } = useData<TenantUser[]>(
    () => tenant && `/web/tenants/${tenant.id}/users/`
  );

  const [addUserOpen, setAddUserOpen] = useState(false);
  const [successAddUser, setSuccessAddUser] = useState<string | null>(null);

  const [userToRemove, setUserToRemove] = useState<TenantUser | null>(null);
  const [error, setError] = useState<string | null>(null);
  const osoRoles = tenant?.kind === "production";
  const updateUserAction = useTenantAction("update_user");
  const deleteUserAction = useTenantAction("delete_user");
  const createUserAction = useTenantAction("create_user");

  const updateUser = useCallback(
    (user: TenantUser, role: string): Promise<void> => {
      if (!tenant) return Promise.resolve();

      return new Promise((resolve, reject) => {
        fetch(`/web/tenants/${tenant.id}/users`, {
          method: "PUT",
          body: JSON.stringify({
            external_user_id: user.id,
            role,
          }),
        })
          .then((r) => {
            if (!r.ok) {
              if (r.status === 404) {
                return reject("You do not have permission to update the user.");
              }

              return reject(
                "Please contact engineering@osohq.com to update the user."
              );
            }

            return resolve();
          })
          .catch((_) => {
            return reject("Something went wrong. Please try again.");
          });
      });
    },
    [tenant]
  );

  if (!data || !tenant || !user) {
    return <LoadingPage />;
  }

  return (
    <div>
      {error && (
        <div className="flex justify-between mb-3 border border-red-600 rounded px-3 py-2">
          <div>
            <WithIcon
              icon={
                <HiOutlineExclamation
                  className="h-5 w-5 text-red-600"
                  aria-hidden="true"
                />
              }
            >
              <span className="text-red-600 text-sm">{error}</span>
            </WithIcon>
          </div>
          <div>
            <HiX
              className="h-4 w-4 cursor-pointer text-gray-600 m-1"
              aria-hidden="true"
              onClick={() => setTimeout(setError.bind(null, null), 300)}
            />
          </div>
        </div>
      )}
      {successAddUser && (
        <div className="flex justify-between mb-3 border border-green-600 rounded px-3 py-2">
          <div>
            <WithIcon
              icon={
                <HiBadgeCheck
                  className="h-5 w-5 text-green-600"
                  aria-hidden="true"
                />
              }
            >
              <span className="text-green-600 text-sm">
                <span className="font-semibold">{successAddUser}</span> has been
                successfully invited to your organization.
              </span>
            </WithIcon>
          </div>
          <div>
            <HiX
              className="h-4 w-4 cursor-pointer text-gray-600 m-1"
              aria-hidden="true"
              onClick={() => setTimeout(setSuccessAddUser.bind(null), 300)}
            />
          </div>
        </div>
      )}
      <div className="grid grid-cols-8 gap-4 text-sm text-gray-600">
        <div className="col-span-3">User</div>
        <div className="col-span-2">{prettyLoginLabel(tenant)}</div>
        <div className="col-span-2">{osoRoles && `Role`}</div>
        <div className="col-span-1"></div>
      </div>
      <div className="border-b pb-1"></div>

      {data
        .sort((a, b) => a.id.localeCompare(b.id))
        .map((tenantUser) => {
          return (
            <div
              key={tenantUser.id}
              className="hover:bg-gray-50 grid grid-cols-8 gap-4 mt-1"
            >
              <div
                className={`col-span-3 pt-1 pb-1 ${
                  tenantUser.pending ? "text-gray-600" : ""
                }`}
              >
                {tenantUser.name}
                {tenantUser.pending && (
                  <span className="ml-2 text-xs rounded border px-2 py-1 shadow-sm border-gray-600">
                    Pending
                  </span>
                )}
              </div>
              <div className="col-span-2 text-sm text-gray-600 pt-1 pb-1">
                <UserDetails tenant={tenant} tenantUser={tenantUser} />
              </div>
              <div className="col-span-2">
                {osoRoles &&
                  (updateUserAction && user.id !== tenantUser.id ? (
                    <RoleSelector
                      user={tenantUser}
                      onChange={updateUser}
                      onError={setError}
                      onUpdate={() => refresh()}
                      className="relative mt-1 mb-1"
                    />
                  ) : (
                    <span>{tenantUser.role}</span>
                  ))}
              </div>
              <div className="text-right col-span-1">
                {deleteUserAction &&
                  tenantUser.id !== user.id &&
                  !tenantUser.owner && (
                    <Button
                      xsmall
                      theme="delete"
                      icon={<HiX />}
                      onClick={() => setUserToRemove(tenantUser)}
                      className="mt-1 mb-1"
                    >
                      Remove
                    </Button>
                  )}
              </div>
            </div>
          );
        })}
      <div className="mt-4" />
      <Button
        medium
        theme="primary"
        icon={<HiOutlineUserAdd />}
        onClick={() => setAddUserOpen(true)}
        className="peer"
        disabled={
          !createUserAction ||
          (tenant.max_users !== null && data.length >= tenant.max_users)
        }
      >
        Invite a user
      </Button>
      {!createUserAction && (
        <div className="hidden peer-hover:block absolute z-10 bg-white shadow-md rounded-md p-4 text-gray-700 text-base text-left">
          You do not have permission to add new users.
        </div>
      )}
      {createUserAction &&
        tenant.max_users !== null &&
        data.length >= tenant.max_users && (
          <div className="hidden peer-hover:block absolute z-10 bg-white shadow-md rounded-md p-4 text-gray-700 text-base text-left">
            You have reached the maximum number of users ({tenant.max_users})
            for a{" "}
            <span className="text-xs text-white rounded font-bold bg-gray-500 uppercase py-0.5 px-1">
              developer
            </span>{" "}
            organization.
          </div>
        )}
      <AddUserModal
        isOpen={addUserOpen}
        onClose={() => setAddUserOpen(false)}
        tenant={tenant}
        placeholderLogin={user.login_identifier}
        onAdd={(login: string) => {
          setSuccessAddUser(login);
          refresh();
          setTimeout(() => setSuccessAddUser(null), 5000);
        }}
      />
      <RemoveUserModal
        tenant={tenant}
        userToRemove={userToRemove}
        onClose={() => setUserToRemove(null)}
        onRemove={() => refresh()}
      />
    </div>
  );
};

const UserManagementView = () => {
  const { tenant } = useTenant();

  if (!tenant) {
    return <LoadingPage />;
  }

  return (
    <div>
      <h2 className="text-2xl font-space mb-6">Users</h2>
      <UserManagement />
    </div>
  );
};

export default UserManagementView;
